import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { AxiosError } from "axios"

import {
  OrganizationsRequests,
  OrganizationsResponses,
} from "@tfg/types/endpoints/organizations"
import {
  createDisplaySettingValidationType,
  updateDisplaySettingValidationType,
} from "@tfg/types/schemas/displaySettings"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"

export const useGetOrganizationSettingsQuery = (organizationId?: number) =>
  useQuery({
    queryKey: ["organization", organizationId],
    enabled: !!organizationId,
    queryFn: async () =>
      (
        await httpClient.get<
          Omit<OrganizationsResponses.Organization, "users">
        >(`/organizations/${organizationId}/settings/`)
      ).data,
  })

export const useUpdateOutPutCustomizationMutation = (
  displaySettingsId: string,
  organizationId?: number
) =>
  useMutation<
    unknown,
    unknown,
    {
      update: OrganizationsRequests.UpdateDisplaySettings
      outputLogoFile: File | null
    }
  >({
    mutationFn: async ({ update, outputLogoFile }) => {
      if (outputLogoFile) {
        const formData = new FormData()
        formData.append("file", outputLogoFile)
        const outputLogo = (
          await httpClient.post(
            `/organizations/display-settings/${displaySettingsId}/upload-logo`,
            formData
          )
        ).data.file
        if (outputLogo) {
          update.outputLogoUrl = outputLogo
        }
      }

      return (
        await httpClient.patch<OrganizationsResponses.Organization>(
          `/organizations/display-settings/${displaySettingsId}`,
          update
        )
      ).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["organization", organizationId])
    },
  })

export const useCreateDisplaySettingsMutation = (
  organizationId: number,
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    createDisplaySettingValidationType
  >
) => {
  return useMutation<unknown, AxiosError, createDisplaySettingValidationType>({
    ...options,
    mutationFn: async (data) => {
      return (await httpClient.post("admin/display-settings", data)).data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["organization", organizationId])
    },
  })
}

export const useUpdateDisplaySettingsMutation = (
  organizationId: number,
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    updateDisplaySettingValidationType
  >
) => {
  return useMutation<unknown, AxiosError, updateDisplaySettingValidationType>({
    ...options,
    mutationFn: async (data) => {
      return (await httpClient.patch("admin/display-settings", data)).data
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      queryClient.invalidateQueries(["organization", organizationId])
    },
  })
}
